import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Grid, Button, Alert, CircularProgress } from "@mui/material";
import SnackbarUtil from "@/utils/SnackbarUtil";
import { useTranslation } from "react-i18next";
import AnalyticsService, {
  OvertimeTrendData,
  EmployeeTrendData,
  EmployeeOvertimeListData,
} from "@/services/att/services/AnalyticsService";
import OvertimeTrendChart from "./OvertimeTrendChart";
import EmployeeTrendChart from "./EmployeeTrendChart";
import OvertimeEmployeeList from "./OvertimeEmployeeList";
import {ReactComponent as NoSubscription} from "@/assets/icons/Nosubscription.svg";

// ── Workforce Analytics Dashboard Component ────────────

const WorkforceAnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();

  // Subscription state
  const [subscribed, setSubscribed] = useState<boolean | null>(null);

  // Chart data state
  const [overtimeTrend, setOvertimeTrend] = useState<OvertimeTrendData | null>(null);
  const [employeeTrend, setEmployeeTrend] = useState<EmployeeTrendData | null>(null);

  // Employee list state
  const [employeeList, setEmployeeList] = useState<EmployeeOvertimeListData | null>(null);
  const [employeePage, setEmployeePage] = useState(1);
  const employeePageSize = 10;

  // Loading states
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Convert any thrown value to a readable string (avoid "[object Object]")
  const getReadableError = (err: any): string => {
    const msg = err?.message;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (msg !== undefined && msg !== null) {
      try {
        return typeof msg === 'object' ? JSON.stringify(msg) : String(msg);
      } catch {
        return String(msg);
      }
    }
    if (err !== undefined && err !== null) {
      try {
        return typeof err === 'object' ? JSON.stringify(err) : String(err);
      } catch {
        return String(err);
      }
    }
    return t("Dashboard.Unknown error");
  };

  // Fetch subscription status
  useEffect(() => {
    AnalyticsService.getSubscriptionStatus()
      .then((res) => {
        setSubscribed(res.subscribed);
      })
      .catch(() => {
        setSubscribed(false);
      });
  }, []);

  // Fetch charts
  const fetchTrends = useCallback(() => {
    setLoadingTrends(true);
    Promise.all([
      AnalyticsService.getOvertimeTrend(),
      AnalyticsService.getEmployeeTrend(),
    ])
      .then(([ot, et]) => {
        setOvertimeTrend(ot);
        setEmployeeTrend(et);
      })
      .catch((err) => setError(getReadableError(err)))
      .finally(() => setLoadingTrends(false));
  }, []);

  // Fetch employee list
  const fetchEmployeeList = useCallback(() => {
    setLoadingList(true);
    AnalyticsService.getEmployeeOvertimeList({
      page: employeePage,
      size: employeePageSize,
    })
      .then(setEmployeeList)
      .catch((err) => setError(getReadableError(err)))
      .finally(() => setLoadingList(false));
  }, [employeePage]);

  // Load data when subscribed
  useEffect(() => {
    if (subscribed === true) {
      fetchTrends();
    }
  }, [subscribed, fetchTrends]);

  // Load employee list
  useEffect(() => {
    if (subscribed === true) {
      fetchEmployeeList();
    }
  }, [subscribed, fetchEmployeeList]);

  // Not subscribed state - shows loading spinner
  if (subscribed === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not subscribed state - shows beautiful unlock view
  if (subscribed === false) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Illustration */}
        <Box sx={{ position: "relative", width: "120px", height: "150px", mb: 3 }}>
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              left: "10px",
              width: "96px",
              height: "130px",
              backgroundColor: "#F0F0F0",
              borderRadius: "10px",
              border: "2px solid #E0E0E0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoSubscription/>
          </Box>
        </Box>

        <Typography
          variant="h5"
          sx={{
            color: "#333333",
            fontWeight: 700,
            fontSize: "22px",
            lineHeight: "30px",
            mb: 1.5,
            textAlign: "center",
          }}
        >
          {t("Dashboard.Unlock Workforce Analytics Dashboard")}
        </Typography>

        <Typography
          sx={{
            color: "#888888",
            fontSize: "14px",
            lineHeight: "26px",
            mb: 3,
            maxWidth: "420px",
            textAlign: "center",
          }}
        >
          {t("Dashboard.Get advanced data analysis, exclusive reports, and real-time monitoring")}
          <br />
          {t("Dashboard.Access all features immediately after subscribing.")}
        </Typography>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#3498DB",
            color: "#FFFFFF",
            fontSize: "15px",
            fontWeight: 600,
            py: 1.5,
            px: 6,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": { backgroundColor: "#2980B9" },
          }}
          onClick={() => {
            SnackbarUtil.warning(t("common.Subscribe in App Tips"), {
              anchorOrigin: {vertical: 'top', horizontal: 'center'}
            });
            return;
          }}
        >
          {t("Dashboard.Subscribe in App")}
        </Button>
      </Box>
    );
  }

  // Subscribed state - shows the analytics dashboard
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#F5F6FA",
        p: "8px 16px 16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ flexGrow: 1 }}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>
          {/* Overtime Hours Trend */}
          <Box sx={{ mb: 1.5, display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 220 }}>
            <OvertimeTrendChart
              data={overtimeTrend}
              loading={loadingTrends}
              onRefresh={fetchTrends}
            />
          </Box>

          {/* Number of Employees Trend */}
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 220 }}>
            <EmployeeTrendChart
              data={employeeTrend}
              loading={loadingTrends}
              onRefresh={fetchTrends}
            />
          </Box>
        </Grid>

        {/* RIGHT COLUMN - Employee List */}
        <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column" }}>
          <OvertimeEmployeeList
            data={employeeList}
            loading={loadingList}
            page={employeePage}
            pageSize={employeePageSize}
            onPageChange={setEmployeePage}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkforceAnalyticsDashboard;
